import React, { useEffect } from 'react';
import Icon from '../AppIcon';
import { PORTAL_BASE_URL } from '../../utils/cn';

const LoginModal = ({ isOpen, onClose }) => {
  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e?.target === e?.currentTarget) onClose();
  };

  const handleHomeowner = () => {
    window.open(`${PORTAL_BASE_URL}/user-sign-in`, '_blank', 'noopener,noreferrer');
    onClose();
  };

  const handleContractor = () => {
    window.open(`${PORTAL_BASE_URL}/sign-in`, '_blank', 'noopener,noreferrer');
    onClose();
  };

  const handleRealtor = () => {
    window.open(`${PORTAL_BASE_URL}/user-sign-in`, '_blank', 'noopener,noreferrer');
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(6px)' }}
      onClick={handleOverlayClick}
    >
      <div
        className="relative w-full max-w-sm bg-white rounded-2xl shadow-2xl overflow-hidden"
        style={{ animation: 'loginModalIn 0.25s cubic-bezier(0.34,1.56,0.64,1) both' }}
      >
        {/* Top accent bar */}
        <div className="h-1 w-full" style={{ background: 'linear-gradient(90deg, #07b0e9, #0284c7)' }} />

        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors duration-200"
          aria-label="Close"
        >
          <Icon name="X" size={18} />
        </button>

        {/* Header */}
        <div className="px-8 pt-7 pb-2 text-center">
          <img
            src="/assets/images/Openbooq_blue-1773069231613.png"
            alt="OpenbooQ logo"
            className="h-10 w-auto mx-auto mb-4 object-contain"
          />
          <h2 className="text-xl font-bold text-gray-900 mb-1">Welcome Back</h2>
          <p className="text-sm text-gray-500">Choose how you'd like to sign in or create an account</p>
        </div>

        {/* Options */}
        <div className="px-8 py-6 space-y-3">
          {/* Homeowner */}
          <button
            onClick={handleHomeowner}
            className="role-card w-full group flex items-center gap-4 px-5 py-4 rounded-xl border-2 border-gray-100 bg-white text-left"
            style={{ borderLeftColor: '#07b0e9', borderLeftWidth: '4px' }}
          >
            <div
              className="role-icon flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #07b0e9, #0284c7)' }}
            >
              <Icon name="Home" size={20} className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-gray-900 text-sm">Homeowner</p>
              <p className="text-xs text-gray-500 mt-0.5">Track your project &amp; quotes</p>
            </div>
            <Icon name="ChevronRight" size={18} className="text-gray-300 group-hover:text-[#07b0e9] transition-colors duration-200 flex-shrink-0" />
          </button>

          {/* Realtor & Property Management */}
          <button
            onClick={handleRealtor}
            className="role-card w-full group flex items-center gap-4 px-5 py-4 rounded-xl border-2 border-gray-100 bg-white text-left"
            style={{ borderLeftColor: '#0d9488', borderLeftWidth: '4px' }}
          >
            <div
              className="role-icon flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #0d9488, #0f766e)' }}
            >
              <Icon name="Building2" size={20} className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-gray-900 text-sm">Realtor &amp; Property Management</p>
              <p className="text-xs text-gray-500 mt-0.5">Manage properties &amp; your portal</p>
            </div>
            <Icon name="ChevronRight" size={18} className="text-gray-300 group-hover:text-[#0d9488] transition-colors duration-200 flex-shrink-0" />
          </button>

          {/* Contractor */}
          <button
            onClick={handleContractor}
            className="role-card w-full group flex items-center gap-4 px-5 py-4 rounded-xl border-2 border-gray-100 bg-white text-left"
            style={{ borderLeftColor: '#1e3a5f', borderLeftWidth: '4px' }}
          >
            <div
              className="role-icon flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #0f172a, #1e3a5f)' }}
            >
              <Icon name="HardHat" size={20} className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-gray-900 text-sm">Contractor</p>
              <p className="text-xs text-gray-500 mt-0.5">Manage jobs &amp; your portal</p>
            </div>
            <Icon name="ChevronRight" size={18} className="text-gray-300 group-hover:text-[#1e3a5f] transition-colors duration-200 flex-shrink-0" />
          </button>
        </div>

        {/* Footer removed - no create account option */}
      </div>

      <style>{`
        @keyframes loginModalIn {
          from { opacity: 0; transform: scale(0.92) translateY(12px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes iconBounce {
          0%, 100% { transform: translateY(0) scale(1); }
          30%       { transform: translateY(-5px) scale(1.12); }
          60%       { transform: translateY(-2px) scale(1.06); }
        }
        .role-card {
          transition: transform 0.2s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.2s ease, border-color 0.2s ease, background-color 0.2s ease;
        }
        .role-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 8px 24px rgba(0,0,0,0.10);
          background-color: #f8fffe;
        }
        .role-card:hover .role-icon {
          animation: iconBounce 0.5s cubic-bezier(0.34,1.56,0.64,1) both;
        }
      `}</style>
    </div>
  );
};

export default LoginModal;